import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";

const D = {
  black:"#0A0A0A", white:"#FFFFFF", red:"#FF0000",
  gray50:"#F7F7F7", gray100:"#EEEEEE", gray200:"#DDDDDD",
  gray400:"#AAAAAA", gray600:"#666666", gray800:"#333333",
  green:"#00B341", blue:"#1877F2",
  radius:8, radiusSm:4,
};

const CORE_CATS = [
  {label:"Hair & Beauty", icon:"💇"},
  {label:"Art",           icon:"🎨"},
  {label:"Food",          icon:"🍔"},
  {label:"Fitness",       icon:"💪"},
  {label:"Fashion",       icon:"👗"},
  {label:"Photography",   icon:"📷"},
  {label:"Wellness",      icon:"🧘"},
  {label:"Tech",          icon:"💻"},
  {label:"Tutoring",      icon:"📚"},
  {label:"Events",        icon:"🎉"},
  {label:"Trades",        icon:"🔧"},
  {label:"Other",         icon:"🏷️"},
];

const RADIUS_PRESETS = [
  {label:"1 mile",    value:1},
  {label:"2 miles",   value:2},
  {label:"5 miles",   value:5},
  {label:"10 miles",  value:10},
  {label:"20 miles",  value:20},
  {label:"40 miles",  value:40},
  {label:"Custom",    value:"custom"},
];

const fmtMiles = m => m === 1 ? "1 mile" : `${m % 1 === 0 ? m : m.toFixed(1)} miles`;

export default function OnboardingScreen({ onComplete }) {
  const [step, setStep] = useState(0);
  const TOTAL = 2;
  const progress = (step / TOTAL) * 100;

  // Profile fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [username, setUsername]   = useState("");

  // Location
  const [search, setSearch]           = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [radiusPreset, setRadiusPreset]   = useState(5);
  const [customRadius, setCustomRadius]   = useState("");
  const [showCustom, setShowCustom]       = useState(false);
  const [locLoading, setLocLoading]       = useState(false);
  const [mapKey, setMapKey]               = useState(0);
  const [mapParams, setMapParams]         = useState(null);
  const debounceRef = useRef(null);
  const sliderDebounce = useRef(null);
  const usernameDebounce = useRef(null);

  // "idle" | "checking" | "available" | "taken" | "short" | "invalid"
  const [usernameStatus, setUsernameStatus] = useState("idle");

  const radius = showCustom ? (parseFloat(customRadius) || 5) : radiusPreset;

  // Interests
  const [interests, setInterests] = useState(new Set());
  const [saving, setSaving]       = useState(false);

  const buildMapUrl = (lat, lng, r) => {
    const deg = Math.max(r * 0.016, 0.005);
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng-deg*1.5},${lat-deg},${lng+deg*1.5},${lat+deg}&layer=mapnik&marker=${lat},${lng}`;
  };

  const setMap = useCallback((lat, lng, r) => {
    setMapParams({ lat, lng, r });
    setMapKey(k => k + 1);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user?.user_metadata) return;
      const meta = user.user_metadata;
      const fullName = meta.full_name || meta.name || "";
      if (fullName && !firstName) {
        const parts = fullName.trim().split(" ");
        setFirstName(parts[0] || "");
        setLastName(parts.slice(1).join(" ") || "");
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude: lat, longitude: lng } = pos.coords;
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
        const data = await res.json();
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || "Your area";
        const state = data.address?.state_code || "";
        const label = state ? `${city}, ${state}` : city;
        setSelectedPlace({ label, lat, lng });
        setSearch(label);
        setMap(lat, lng, 5);
      } catch {}
      setLocLoading(false);
    }, () => setLocLoading(false));
  }, []);

  useEffect(() => {
    if (!search || search.length < 3) { setSuggestions([]); return; }
    if (selectedPlace && search === selectedPlace.label) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(search)}&format=json&limit=5`);
        const data = await res.json();
        setSuggestions(data.map(r => ({
          label: r.display_name.split(",").slice(0,3).join(",").trim(),
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon),
        })));
      } catch {}
    }, 400);
  }, [search]);

  const pickPlace = (place) => {
    setSelectedPlace(place);
    setSearch(place.label);
    setSuggestions([]);
    setMap(place.lat, place.lng, radius);
  };

  const detectLocation = () => {
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      const { latitude: lat, longitude: lng } = pos.coords;
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
        const data = await res.json();
        const city = data.address?.city || data.address?.town || data.address?.village || "Your area";
        const state = data.address?.state_code || "";
        const label = state ? `${city}, ${state}` : city;
        pickPlace({ label, lat, lng });
      } catch {}
      setLocLoading(false);
    }, () => setLocLoading(false));
  };

  const handleRadiusSelect = (val) => {
    if (val === "custom") {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      setRadiusPreset(val);
      if (selectedPlace) {
        clearTimeout(sliderDebounce.current);
        sliderDebounce.current = setTimeout(() => setMap(selectedPlace.lat, selectedPlace.lng, val), 200);
      }
    }
  };

  const handleCustomRadius = (val) => {
    setCustomRadius(val);
    const num = parseFloat(val);
    if (num > 0 && selectedPlace) {
      clearTimeout(sliderDebounce.current);
      sliderDebounce.current = setTimeout(() => setMap(selectedPlace.lat, selectedPlace.lng, num), 400);
    }
  };

  const handleUsernameChange = (raw) => {
    const val = raw.replace(/[@\s]/g, "").toLowerCase();
    setUsername(val);
    clearTimeout(usernameDebounce.current);
    if (!val) { setUsernameStatus("idle"); return; }
    if (val.length < 3) { setUsernameStatus("short"); return; }
    if (!/^[a-z0-9_]+$/.test(val)) { setUsernameStatus("invalid"); return; }
    setUsernameStatus("checking");
    usernameDebounce.current = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from("users").select("id").eq("handle", `@${val}`).maybeSingle();
        setUsernameStatus(data ? "taken" : "available");
      } catch { setUsernameStatus("available"); }
    }, 500);
  };

  const toggleInterest = (label) => setInterests(s => {
    const n = new Set(s);
    if (n.has(label)) n.delete(label);
    else n.add(label);
    return n;
  });

  const canNext = () => {
    if (step === 0) return !!selectedPlace && !!firstName.trim() && usernameStatus === "available";
    return interests.size >= 1;
  };

  const next = () => {
    if (step < TOTAL - 1) { setStep(s => s + 1); return; }
    handleFinish();
  };

  const [saveError, setSaveError] = useState("");

  const handleFinish = async () => {
    setSaving(true);
    setSaveError("");
    let userId = null;
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Session expired — please sign in again.");
      userId = authUser.id;
      const { error } = await supabase.from("users").upsert({
        id: authUser.id,
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        handle: username.startsWith("@") ? username : `@${username}`,
        interests: [...interests],
        role: "buyer",
      }, { onConflict: "id" });
      if (error) throw error;
    } catch (e) {
      setSaveError(e.message || "Failed to save profile. Please try again.");
      setSaving(false);
      return;
    }
    setSaving(false);
    onComplete({ id: userId });
  };

  return (
    <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",background:D.white,fontFamily:"'DM Sans',sans-serif",display:"flex",flexDirection:"column"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        .fu{animation:fadeUp .22s ease both}
        .tap{cursor:pointer;-webkit-tap-highlight-color:transparent}
        .tap:active{opacity:.75}
        input:focus,select:focus{outline:none}
        .field{width:100%;padding:12px 14px;font-size:14px;font-weight:600;font-family:'DM Sans',sans-serif;color:#0A0A0A;background:#F7F7F7;border:1.5px solid #DDDDDD;border-radius:8px;transition:border-color .15s;}
        .field:focus{border-color:#0A0A0A;background:#fff;}
        select.field{cursor:pointer;-webkit-appearance:none;appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 14px center;background-color:#F7F7F7;padding-right:36px;}
      `}</style>

      {/* Header */}
      <div style={{padding:"48px 24px 0",flexShrink:0}}>
        <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.05em",marginBottom:20}}>
          nearme<span style={{color:D.red}}>.</span>
        </div>
        <div style={{height:3,background:D.gray100,borderRadius:2,marginBottom:6,overflow:"hidden"}}>
          <div style={{height:"100%",background:D.black,borderRadius:2,width:`${progress}%`,transition:"width .3s ease"}}/>
        </div>
        <div style={{fontSize:10,fontWeight:700,color:D.gray400,letterSpacing:"0.06em"}}>STEP {step+1} OF {TOTAL}</div>
      </div>

      {/* ── STEP 0: Profile + Location ── */}
      {step===0 && (
        <div className="fu" style={{flex:1,padding:"24px 24px 0",display:"flex",flexDirection:"column",overflowY:"auto"}}>
          <div style={{marginBottom:20,flexShrink:0}}>
            <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:4}}>Set up your profile</div>
            <div style={{fontSize:13,color:D.gray600,fontWeight:500}}>Tell us who you are and where you're located.</div>
          </div>

          {/* Name row */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:D.gray600,marginBottom:5,letterSpacing:"0.02em"}}>First name</div>
              <input className="field" type="text" placeholder="First" value={firstName} onChange={e=>setFirstName(e.target.value)}/>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:D.gray600,marginBottom:5,letterSpacing:"0.02em"}}>Last name</div>
              <input className="field" type="text" placeholder="Last" value={lastName} onChange={e=>setLastName(e.target.value)}/>
            </div>
          </div>

          {/* Username */}
          <div style={{marginBottom:20}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
              <div style={{fontSize:11,fontWeight:700,color:D.gray600,letterSpacing:"0.02em"}}>Username</div>
              {usernameStatus==="checking" && <span style={{fontSize:10,fontWeight:600,color:D.gray400}}>Checking…</span>}
              {usernameStatus==="available" && <span style={{fontSize:10,fontWeight:700,color:D.green}}>✓ Available</span>}
              {usernameStatus==="taken"     && <span style={{fontSize:10,fontWeight:700,color:D.red}}>✗ Already taken</span>}
              {usernameStatus==="short"     && <span style={{fontSize:10,fontWeight:600,color:D.gray400}}>Min 3 characters</span>}
              {usernameStatus==="invalid"   && <span style={{fontSize:10,fontWeight:700,color:D.red}}>Letters, numbers & _ only</span>}
            </div>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:14,fontWeight:700,color:D.gray400}}>@</span>
              <input className="field" type="text" placeholder="yourhandle" value={username}
                onChange={e=>handleUsernameChange(e.target.value)}
                style={{paddingLeft:28,borderColor:usernameStatus==="available"?D.green:usernameStatus==="taken"||usernameStatus==="invalid"?D.red:undefined}}/>
            </div>
            <div style={{fontSize:10,color:D.gray400,fontWeight:500,marginTop:5}}>This is your unique tag — shown on your profile and listings.</div>
          </div>

          {/* Divider */}
          <div style={{height:1,background:D.gray100,marginBottom:20}}/>

          {/* Location label */}
          <div style={{fontSize:15,fontWeight:800,marginBottom:12,letterSpacing:"-0.01em"}}>Location</div>

          {/* Search */}
          <div style={{position:"relative",marginBottom:8,flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,border:`1.5px solid ${selectedPlace?D.black:D.gray200}`,borderRadius:D.radius,padding:"11px 14px",background:D.white,transition:"border-color .15s"}}>
              <span style={{fontSize:15,flexShrink:0}}>{locLoading?"⏳":"📍"}</span>
              <input value={search} onChange={e=>{setSearch(e.target.value);setSelectedPlace(null);}}
                placeholder="Search by city, neighborhood or ZIP code"
                style={{flex:1,border:"none",fontSize:14,fontWeight:500,fontFamily:"'DM Sans',sans-serif",color:D.black,background:"none"}}/>
              {search && <span className="tap" onClick={()=>{setSearch("");setSelectedPlace(null);setSuggestions([]);}} style={{fontSize:13,color:D.gray400,cursor:"pointer",flexShrink:0}}>✕</span>}
            </div>
            {suggestions.length>0 && (
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:D.white,border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,boxShadow:"0 4px 20px rgba(0,0,0,0.12)",zIndex:100,marginTop:4}}>
                {suggestions.map((s,i) => (
                  <div key={i} className="tap" onClick={()=>pickPlace(s)}
                    style={{padding:"11px 14px",fontSize:13,fontWeight:600,borderBottom:i<suggestions.length-1?`1px solid ${D.gray100}`:"none",cursor:"pointer",color:D.gray800}}>
                    📍 {s.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Detect button */}
          {!selectedPlace && !locLoading && (
            <button onClick={detectLocation}
              style={{display:"flex",alignItems:"center",gap:8,padding:"11px 14px",border:`1.5px solid ${D.gray200}`,borderRadius:D.radius,background:D.white,fontSize:13,fontWeight:700,color:D.gray800,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",marginBottom:8}}>
              🎯 <span>Use current location</span>
            </button>
          )}

          {/* Radius dropdown */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:D.gray600,marginBottom:5,letterSpacing:"0.02em"}}>Radius</div>
            <select className="field"
              value={showCustom?"custom":radiusPreset}
              onChange={e=>handleRadiusSelect(e.target.value==="custom"?"custom":parseFloat(e.target.value))}>
              {RADIUS_PRESETS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {showCustom && (
              <div style={{marginTop:8,display:"flex",alignItems:"center",gap:8}}>
                <input className="field" type="number" min="0.1" max="100" step="0.5"
                  placeholder="Enter miles (e.g. 7.5)"
                  value={customRadius} onChange={e=>handleCustomRadius(e.target.value)}
                  style={{flex:1}}/>
                <span style={{fontSize:13,fontWeight:600,color:D.gray600,flexShrink:0}}>miles</span>
              </div>
            )}
          </div>

          {/* Map */}
          {mapParams ? (
            <div style={{borderRadius:D.radius,overflow:"hidden",border:`1.5px solid ${D.gray200}`,height:240,position:"relative",marginBottom:24,flexShrink:0}}>
              <iframe
                key={mapKey}
                src={buildMapUrl(mapParams.lat, mapParams.lng, mapParams.r)}
                title="map"
                style={{width:"100%",height:"100%",border:"none",display:"block"}}
                loading="lazy"
              />
              {/* Bottom pill */}
              <div style={{position:"absolute",bottom:10,left:"50%",transform:"translateX(-50%)",background:"rgba(10,10,10,0.82)",backdropFilter:"blur(6px)",borderRadius:20,padding:"5px 14px",display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap",pointerEvents:"none"}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:D.red,flexShrink:0,animation:"pulse 1.4s ease-in-out infinite"}}/>
                <span style={{fontSize:11,fontWeight:700,color:D.white}}>{fmtMiles(radius)} · {selectedPlace?.label}</span>
              </div>
            </div>
          ) : !locLoading ? (
            <div style={{borderRadius:D.radius,border:`1.5px dashed ${D.gray200}`,display:"flex",alignItems:"center",justifyContent:"center",height:200,flexDirection:"column",gap:10,marginBottom:24}}>
              <span style={{fontSize:36}}>🗺️</span>
              <span style={{fontSize:12,color:D.gray400,fontWeight:600,textAlign:"center"}}>Map appears after you select a location</span>
            </div>
          ) : (
            <div style={{borderRadius:D.radius,border:`1.5px solid ${D.gray100}`,display:"flex",alignItems:"center",justifyContent:"center",height:200,marginBottom:24}}>
              <span style={{fontSize:13,color:D.gray400,fontWeight:600}}>Detecting location…</span>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 1: Interests ── */}
      {step===1 && (
        <div className="fu" style={{flex:1,overflowY:"auto",paddingBottom:20}}>
          <div style={{padding:"24px 24px 20px"}}>
            <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.04em",marginBottom:4}}>What are you into?</div>
            <div style={{fontSize:13,color:D.gray600,fontWeight:500}}>Pick the categories you care about.</div>
          </div>

          <div style={{
            display:"grid",
            gridTemplateColumns:"repeat(3, 1fr)",
            gap:8,
            padding:"0 24px",
          }}>
            {CORE_CATS.map(i => {
              const sel = interests.has(i.label);
              return (
                <div key={i.label} className="tap" onClick={() => toggleInterest(i.label)}
                  style={{
                    display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                    gap:7,borderRadius:12,padding:"18px 8px",cursor:"pointer",
                    background:sel?D.black:D.white,
                    border:`1.5px solid ${sel?D.black:D.gray200}`,
                    transition:"all .12s",
                  }}>
                  <span style={{fontSize:26,lineHeight:1}}>{i.icon}</span>
                  <span style={{fontSize:11,fontWeight:700,color:sel?D.white:D.black,textAlign:"center",lineHeight:1.2}}>{i.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom nav */}
      <div style={{padding:"16px 24px 36px",borderTop:`1px solid ${D.gray100}`,background:D.white,flexShrink:0}}>
        {step===1 && (
          <div className="tap" onClick={()=>setStep(0)} style={{textAlign:"center",fontSize:12,color:D.gray400,fontWeight:600,marginBottom:12,cursor:"pointer",textDecoration:"underline"}}>← Back</div>
        )}
        {step===0 && !canNext() && (
          <div style={{fontSize:11,color:D.gray400,fontWeight:600,textAlign:"center",marginBottom:8}}>
            {!firstName.trim() ? "Enter your first name"
              : usernameStatus==="idle"    ? "Choose a username"
              : usernameStatus==="short"   ? "Username needs at least 3 characters"
              : usernameStatus==="invalid" ? "Username: letters, numbers & _ only"
              : usernameStatus==="taken"   ? "That username is taken — try another"
              : usernameStatus==="checking"? "Checking username…"
              : "Pick your location to continue"}
          </div>
        )}
        {saveError && <div style={{fontSize:12,color:"#FF0000",fontWeight:600,marginBottom:10,padding:"10px 12px",background:"#FFF0F0",borderRadius:6,border:"1px solid #FFCCCC"}}>{saveError}</div>}
        <button onClick={next} disabled={!canNext()||saving}
          style={{width:"100%",padding:"15px",borderRadius:D.radius,border:"none",background:canNext()?D.black:D.gray200,color:canNext()?D.white:D.gray400,fontSize:14,fontWeight:800,fontFamily:"'DM Sans',sans-serif",cursor:canNext()?"pointer":"default",transition:"all .15s",letterSpacing:"0.02em"}}>
          {saving?"Setting up…":step===TOTAL-1?"Let's go →":"Continue →"}
        </button>
      </div>
    </div>
  );
}
